import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PhoneCall, Filter, Download } from "lucide-react";
import { ukDateTime } from "../lib/dates";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const STATUS_STYLE = {
  initiated: { bg: "#E3F1EC", color: "#0B5E4E", label: "Connected" },
  failed: { bg: "#FBE4E4", color: "#B4433B", label: "Failed" },
};

export default function CallingAudit() {
  const [calls, setCalls] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (tid) => {
    setLoading(true);
    try {
      const { data } = await api.get("/calls/audit", { params: tid ? { team_id: tid } : {} });
      setCalls(data.calls); setTeams(data.teams);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); }, []);

  const onFilter = (v) => { setTeamId(v); load(v); };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/calls/audit/export", { params: teamId ? { team_id: teamId } : {}, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `calling-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="calling-audit-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight flex items-center gap-2"><PhoneCall className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Calling Audit</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Who called which member and when. Filter by the member's team.</p>
      </div>

      <div className={`${CARD} p-4 flex flex-wrap items-center gap-3`}>
        <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--sj-text-2)" }}><Filter className="h-4 w-4" /> Team</div>
        <select data-testid="calling-audit-team-filter" value={teamId} onChange={(e) => onFilter(e.target.value)}
          className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
          <option value="">All teams</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className="text-sm ml-auto" style={{ color: "var(--sj-text-3)" }} data-testid="calling-audit-count">{calls.length} calls</span>
        <button data-testid="calling-audit-export-btn" onClick={exportCsv} disabled={exporting || calls.length === 0}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
          <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      <div className={`${CARD} p-2 sm:p-4 overflow-x-auto`}>
        {loading ? (
          <div className="text-center py-10" style={{ color: "var(--sj-text-3)" }}>Loading…</div>
        ) : calls.length === 0 ? (
          <div className="text-center py-10" style={{ color: "var(--sj-text-3)" }}>No calls recorded{teamId ? " for this team" : ""} yet.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead><tr className="text-left" style={{ color: "var(--sj-text-3)" }}>
              <th className="py-3 px-3">Member</th>
              <th className="py-3 px-3">Called by</th>
              <th className="py-3 px-3">When</th>
              <th className="py-3 px-3">Teams</th>
              <th className="py-3 px-3">Status</th>
            </tr></thead>
            <tbody>
              {calls.map((c) => {
                const ss = STATUS_STYLE[c.status] || { bg: "#EDF2F4", color: "var(--sj-text-2)", label: c.status || "—" };
                return (
                  <tr key={c.id} data-testid={`call-row-${c.id}`} className="border-t" style={{ borderColor: "var(--sj-border)" }}>
                    <td className="py-2.5 px-3 font-medium">{c.member_name}</td>
                    <td className="py-2.5 px-3">{c.by}</td>
                    <td className="py-2.5 px-3" style={{ color: "var(--sj-text-2)" }}>{c.at ? ukDateTime(c.at) : "—"}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.teams || []).length === 0 ? <span style={{ color: "var(--sj-text-3)" }}>—</span> :
                          c.teams.map((t, i) => <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--sj-peach)", color: "var(--sj-primary)" }}>{t}</span>)}
                      </div>
                    </td>
                    <td className="py-2.5 px-3"><span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: ss.bg, color: ss.color }}>{ss.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
